import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Canvas, useThree, useFrame } from "@react-three/fiber";
import { Stars, OrbitControls } from "@react-three/drei";
import { Constellation } from "@chuzi/shared/realms/cosmos/components";
import { cosmosMapping } from "@chuzi/shared/realms/cosmos";
import { distributeStars } from "@chuzi/shared/realms/cosmos/components";
import type {
  CatalogResponse,
  PublicDirectorProfile,
  StoryListItem,
  RealmId,
} from "@chuzi/shared/types";
import { api } from "../lib/api";
import { DirectorAvatar } from "../components/director-avatar";
import { StoryHoverHud } from "../components/story-hover-hud";
import * as THREE from "three";

type PlacedStory = {
  story: StoryListItem;
  visual: ReturnType<typeof cosmosMapping> & {
    position: [number, number, number];
  };
};

export function DirectorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [director, setDirector] = useState<PublicDirectorProfile | null>(null);
  const [catalog, setCatalog] = useState<CatalogResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [hudScreen, setHudScreen] = useState<{ x: number; y: number } | null>(
    null,
  );

  const films = catalog?.data ?? null;
  const realm: RealmId = director?.realm === "wilds" ? "wilds" : "cosmos";

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    api.user
      .publicProfile(id)
      .then((profile) => {
        if (!cancelled) setDirector(profile);
      })
      .catch(() => {
        if (!cancelled) setError("Director not found");
      });
    api.catalog
      .byCreator(id)
      .then((res) => {
        if (!cancelled) setCatalog(res);
      })
      .catch(() => {
        if (!cancelled) setError("Could not load films");
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const placed: PlacedStory[] = useMemo(() => {
    if (!films || films.length === 0) return [];
    const positions = distributeStars(films.length, { seed: 2, radius: 18 });
    return films.map((film, i) => ({
      story: film,
      visual: { ...cosmosMapping(film), position: positions[i] },
    }));
  }, [films]);

  const onSelect = useCallback(
    (index: number) => setFocusedIndex(index),
    [],
  );

  const focusedItem = placed[focusedIndex] ?? null;
  const showHud = focusedItem != null && hudScreen != null;

  const placedScene = focusedItem
    ? {
        id: focusedItem.story.id,
        story_id: focusedItem.story.id,
        title: focusedItem.story.title,
        order: 0,
        is_title: true as const,
        is_end: false as const,
        color: null,
        media_id: null,
        alt_media_id: null,
        media_mode: null,
        text_content: null,
        text_style: null,
        created_at: focusedItem.story.created_at,
        updated_at: focusedItem.story.updated_at,
      }
    : null;

  if (error) {
    return (
      <div
        style={{
          width: "100vw",
          height: "100vh",
          background: "#04070d",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "rgba(232,240,255,0.6)",
          fontSize: 16,
        }}
      >
        {error}
      </div>
    );
  }

  return (
    <div
      style={{
        position: "relative",
        width: "100vw",
        height: "100vh",
        background: "#04070d",
      }}
    >
      {films ? (
        <DirectorCosmosScene
          placed={placed}
          focusedIndex={focusedIndex}
          onHudProject={setHudScreen}
          onSelect={onSelect}
        />
      ) : (
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "rgba(232,240,255,0.4)",
            fontSize: 14,
            letterSpacing: 1,
          }}
        >
          Loading...
        </div>
      )}

      {showHud && focusedItem && placedScene ? (
        <StoryHoverHud
          story={focusedItem.story}
          scene={placedScene}
          screenX={hudScreen.x}
          screenY={hudScreen.y}
          realm={realm}
          coverboxUrl={catalog?.meta.coverboxes[focusedItem.story.id] ?? null}
          creatorAvatarUrl={director?.avatar_url ?? null}
        />
      ) : null}

      {/* Director header */}
      {director ? (
        <div
          style={{
            position: "absolute",
            top: 24,
            left: 0,
            right: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 10,
            pointerEvents: "none",
            zIndex: 30,
          }}
        >
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              overflow: "hidden",
              border: "2px solid rgba(126,184,255,0.3)",
              background: "rgba(10,14,30,0.8)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {director.avatar_url ? (
              <img
                src={director.avatar_url}
                alt={director.name}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              <DirectorAvatar seed={director.id} realm={realm} size={60} />
            )}
          </div>
          <div
            style={{
              fontSize: 18,
              fontWeight: 700,
              color: "#e8f0ff",
              letterSpacing: 0.5,
              textShadow: "0 2px 12px rgba(0,0,0,0.9)",
            }}
          >
            {director.name}
          </div>
          {director.stories_count > 0 ? (
            <div
              style={{
                fontSize: 11,
                color: "rgba(232,240,255,0.45)",
                letterSpacing: 1,
                textTransform: "uppercase",
              }}
            >
              {director.stories_count}{" "}
              {director.stories_count === 1 ? "film" : "films"}
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Back button */}
      <button
        onClick={() => navigate(-1)}
        style={{
          position: "absolute",
          top: 24,
          left: 24,
          zIndex: 30,
          background: "rgba(10,14,30,0.7)",
          border: "1px solid rgba(126,184,255,0.2)",
          borderRadius: 8,
          color: "rgba(232,240,255,0.7)",
          fontSize: 13,
          padding: "6px 14px",
          cursor: "pointer",
          backdropFilter: "blur(8px)",
        }}
      >
        &larr; Back
      </button>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Director cosmos 3D scene                                           */
/* ------------------------------------------------------------------ */

function DirectorCosmosScene({
  placed,
  focusedIndex,
  onHudProject,
  onSelect,
}: {
  placed: PlacedStory[];
  focusedIndex: number;
  onHudProject: (pos: { x: number; y: number } | null) => void;
  onSelect: (index: number) => void;
}) {
  return (
    <Canvas
      camera={{ position: [0, 6, 20], fov: 60, near: 0.1, far: 500 }}
      dpr={[1, 2]}
      gl={{ antialias: true }}
      style={{ width: "100%", height: "100%" }}
    >
      <color attach="background" args={["#04070d"]} />
      <ambientLight intensity={0.15} />
      <Stars
        radius={120}
        depth={60}
        count={3000}
        factor={4}
        fade
        saturation={0.4}
      />

      {placed.map(({ story, visual }, i) => (
        <Constellation
          key={`constellation-${story.id}`}
          scenes={[{ id: story.id, visual }]}
          onSceneSelect={() => onSelect(i)}
        />
      ))}

      {placed.length > 0 && focusedIndex < placed.length ? (
        <DirectorFocusRing
          position={placed[focusedIndex].visual.position}
          color="#7eb8ff"
        />
      ) : null}

      <DirectorCameraController
        placed={placed}
        focusedIndex={focusedIndex}
        onHudProject={onHudProject}
      />
    </Canvas>
  );
}

/* ------------------------------------------------------------------ */
/*  Camera controller                                                  */
/* ------------------------------------------------------------------ */

function DirectorCameraController({
  placed,
  focusedIndex,
  onHudProject,
}: {
  placed: PlacedStory[];
  focusedIndex: number;
  onHudProject: (pos: { x: number; y: number } | null) => void;
}) {
  const { camera, size } = useThree();
  const targetPos = useRef(new THREE.Vector3(0, 4, 16));
  const targetLookAt = useRef(new THREE.Vector3(0, 0, 0));
  const controlsRef = useRef<any>(null);

  useFrame(() => {
    let goalPos: THREE.Vector3;
    let goalLookAt: THREE.Vector3;

    if (placed.length > 0 && focusedIndex < placed.length) {
      const p = placed[focusedIndex].visual.position;
      goalLookAt = new THREE.Vector3(p[0], p[1], p[2]);
      goalPos = new THREE.Vector3(p[0], p[1] + 4, p[2] + 12);

      const projected = new THREE.Vector3(p[0], p[1] + 0.5, p[2]);
      projected.project(camera);
      const sx = (projected.x * 0.5 + 0.5) * size.width;
      const sy = (-projected.y * 0.5 + 0.5) * size.height;
      onHudProject({ x: sx, y: sy });
    } else {
      goalPos = new THREE.Vector3(0, 6, 20);
      goalLookAt = new THREE.Vector3(0, 0, 0);
      onHudProject(null);
    }

    targetPos.current.lerp(goalPos, 0.04);
    targetLookAt.current.lerp(goalLookAt, 0.04);
    camera.position.copy(targetPos.current);
    camera.lookAt(targetLookAt.current);

    if (controlsRef.current) {
      controlsRef.current.target.copy(targetLookAt.current);
      controlsRef.current.update();
    }
  });

  return (
    <OrbitControls
      ref={controlsRef}
      enableDamping
      dampingFactor={0.08}
      minDistance={2}
      maxDistance={50}
    />
  );
}

/* ------------------------------------------------------------------ */
/*  Focus ring                                                         */
/* ------------------------------------------------------------------ */

function DirectorFocusRing({
  position,
  color,
}: {
  position: [number, number, number];
  color: string;
}) {
  const ref = useRef<THREE.Mesh>(null);

  useFrame(({ clock }) => {
    if (!ref.current) return;
    const pulse = 1 + Math.sin(clock.elapsedTime * 2) * 0.15;
    ref.current.scale.set(pulse, 1, pulse);
  });

  return (
    <mesh ref={ref} position={position} rotation={[-Math.PI / 2, 0, 0]}>
      <ringGeometry args={[1.6, 1.8, 64]} />
      <meshBasicMaterial color={color} transparent opacity={0.35} />
    </mesh>
  );
}
